import React from "react";
import { useTranslation } from "react-i18next";
import { SubPage, FullDivider } from "./SubPage";

const BRAND = "#FF3C00";

type LinkDef = { label: string; href: string; external?: boolean };

interface ProductMeta {
  logo?: string;
  invertLogo?: boolean;
  heroImage?: string;
  website?: string;
  links?: LinkDef[];
  gallery?: string[];
}

const META: Record<string, ProductMeta> = {
  cate: {
    logo: "/assets/cate-logo.svg",
    heroImage: "/assets/cate-screenshot-dark.png",
    website: "https://cate.cero-ai.com",
    links: [
      { label: "cate.cero-ai.com", href: "https://cate.cero-ai.com", external: true },
      { label: "GitHub", href: "https://github.com/0-AI-UG/cate", external: true },
      { label: "Product Hunt", href: "https://www.producthunt.com/products/cate", external: true },
      { label: "Download", href: "https://github.com/0-AI-UG/cate/releases/latest", external: true },
    ],
    gallery: [
      "/assets/cate-screenshot-light.png",
      "/assets/cate-screenshot-dark.png",
    ],
  },
  "one-click-deploy": {
    logo: "/assets/one-click-deploy-logo.svg",
    website: "https://github.com/0-AI-UG/open-cli-deployment",
  },
  "ai-overlay": {},
  "website-rework": {},
};

const ExternalIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="opacity-60 shrink-0">
    <path d="M1 11L11 1M11 1H4M11 1V8" stroke="currentColor" strokeWidth="1.2" />
  </svg>
);

export function ProductPage({ slug }: { slug: string }) {
  const { t } = useTranslation("productDetails");
  const { t: tp } = useTranslation("productPage");
  if (!META[slug]) return null;
  const meta = META[slug]!;
  const title = t(`${slug}.title`);
  const subtitle = t(`${slug}.subtitle`);
  const sections = t(`${slug}.sections`, { returnObjects: true }) as { heading: string; body: string }[];

  const hasLinks = meta.links && meta.links.length > 0;
  const hasGallery = meta.gallery && meta.gallery.length > 0;
  const hasHighlights = t(`${slug}.highlights`, { returnObjects: true, defaultValue: null }) as string[] | null;
  const tagline = t(`${slug}.tagline`, { defaultValue: "" });

  return (
    <SubPage title={title} subtitle={subtitle} logo={meta.logo} invertLogo={meta.invertLogo} heroImage={meta.heroImage}>
      {/* Links bar */}
      {hasLinks ? (
        <>
          <div className="px-5 sm:px-8 md:px-[66px] py-5 bg-grey-975 flex flex-wrap items-center gap-4 sm:gap-6">
            {meta.links!.map((link) => (
              <a
                key={link.href}
                href={link.href}
                target={link.external ? "_blank" : undefined}
                rel={link.external ? "noopener noreferrer" : undefined}
                className="inline-flex items-center gap-1.5 text-sm text-white/60 hover:text-white transition-colors"
              >
                {link.label}
                {link.external && <ExternalIcon />}
              </a>
            ))}
          </div>
          <FullDivider />
        </>
      ) : meta.website ? (
        <>
          <div className="px-5 sm:px-8 md:px-[66px] py-5 bg-grey-975">
            <a
              href={meta.website}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors"
            >
              <ExternalIcon />
              {meta.website.replace(/^https?:\/\//, "")}
            </a>
          </div>
          <FullDivider />
        </>
      ) : null}

      {/* Tagline + highlights */}
      {(tagline || hasHighlights) && (
        <>
          <div className="px-5 sm:px-8 md:px-[66px] py-12 md:py-16 bg-grey-975">
            {tagline && (
              <p className="text-xl md:text-2xl font-bold text-white tracking-tight mb-8 max-w-2xl leading-tight">
                {tagline}
              </p>
            )}
            {hasHighlights && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {hasHighlights.map((h) => (
                  <div key={h} className="flex items-start gap-3 p-4" style={{ border: "1px solid #27272a" }}>
                    <span className="font-bold mt-0.5 shrink-0 text-xs" style={{ color: BRAND }}>✓</span>
                    <span className="text-[14px] text-grey-300 leading-snug">{h}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <FullDivider />
        </>
      )}

      {/* Sections grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-grey-800">
        {sections.map((s) => (
          <div key={s.heading} className="bg-grey-975 px-5 sm:px-8 md:px-[66px] py-10 md:py-12">
            <h3 className="text-base font-semibold text-white mb-3">{s.heading}</h3>
            <p className="text-sm text-grey-400 leading-relaxed">{s.body}</p>
          </div>
        ))}
      </div>
      <FullDivider />

      {/* Gallery */}
      {hasGallery && (
        <>
          <div className="px-5 sm:px-8 md:px-[66px] py-12 md:py-16 bg-grey-975">
            <h3 className="text-[11px] font-mono uppercase tracking-widest text-grey-500 mb-8">
              {tp("galleryLabel")}
            </h3>
            <div className="flex flex-col gap-4">
              {meta.gallery!.map((src) => (
                <div key={src} className="rounded-xl overflow-hidden" style={{ border: "1px solid #27272a" }}>
                  <img src={src} alt="" className="w-full object-cover" />
                </div>
              ))}
            </div>
          </div>
          <FullDivider />
        </>
      )}

      {/* Download CTA for products with download links */}
      {meta.links?.some((l) => l.label === "Download") && (
        <>
          <div className="px-5 sm:px-8 md:px-[66px] py-16 md:py-20 bg-grey-975 text-center">
            <h3 className="text-2xl md:text-3xl font-bold text-white tracking-tight mb-4">
              {tp("downloadTitle")}
            </h3>
            <p className="text-sm text-grey-400 mb-8 max-w-md mx-auto">
              {tp("downloadLead")}
            </p>
            <div className="flex flex-wrap justify-center items-center gap-3">
              <a
                href="https://github.com/0-AI-UG/cate/releases/latest"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-white text-sm font-medium px-7 py-2.5 rounded-full transition-colors"
                style={{ backgroundColor: BRAND }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#e63600")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = BRAND)}
              >
                {tp("downloadBtn")}
              </a>
              <a
                href="https://github.com/0-AI-UG/cate"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-grey-200 text-sm font-medium px-7 py-2.5 rounded-full transition-colors border border-grey-700 hover:border-grey-500 hover:text-white"
              >
                GitHub <ExternalIcon />
              </a>
              <a
                href="https://www.producthunt.com/products/cate"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-grey-200 text-sm font-medium px-7 py-2.5 rounded-full transition-colors border border-grey-700 hover:border-grey-500 hover:text-white"
              >
                Product Hunt <ExternalIcon />
              </a>
            </div>
          </div>
          <FullDivider />
        </>
      )}
    </SubPage>
  );
}
